import { authClient } from "@d12labs/auth/client";
import { Button } from "@d12labs/ui/button";
import { Input } from "@d12labs/ui/input";
import { Label } from "@d12labs/ui/label";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { AuthLayout } from "@/components/pages/auth-layout";

export function ResetPasswordPage() {
	const { token } = useSearch({ from: "/reset-password" });
	const navigate = useNavigate();
	const [newPassword, setNewPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	if (!token) {
		return (
			<AuthLayout
				title="Invalid link"
				description="This reset link is missing its token."
				footer={
					<Link to="/forgot-password" className="underline">
						Request a new link
					</Link>
				}
			>
				<p className="text-sm text-muted-foreground">
					Reset links expire after an hour and can only be used once. If yours stopped working,
					request a fresh one.
				</p>
			</AuthLayout>
		);
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setSubmitting(true);
		const { error: err } = await authClient.resetPassword({ newPassword, token });
		setSubmitting(false);
		if (err) {
			setError(err.message ?? "Reset failed");
			return;
		}
		toast.success("Password updated — sign in with your new password.");
		await navigate({ to: "/login" });
	};

	return (
		<AuthLayout
			title="Set new password"
			description="Choose something you can remember."
			footer={
				<Link to="/login" className="underline">
					Back to sign in
				</Link>
			}
		>
			<form onSubmit={handleSubmit} className="flex flex-col gap-4">
				<div className="flex flex-col gap-2">
					<Label htmlFor="newPassword">New password</Label>
					<Input
						id="newPassword"
						type="password"
						value={newPassword}
						onChange={(e) => setNewPassword(e.target.value)}
						required
						minLength={8}
						autoComplete="new-password"
						autoFocus
					/>
					<p className="text-xs text-muted-foreground">Minimum 8 characters.</p>
				</div>
				{error && <p className="text-sm text-destructive">{error}</p>}
				<Button type="submit" disabled={submitting}>
					{submitting ? "Setting…" : "Set password"}
				</Button>
			</form>
		</AuthLayout>
	);
}
